import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Eye, Send, FlaskConical, Save, Image as ImageIcon, Paperclip, X, Users, Trash2 } from 'lucide-react';
import { PageHeader, Field, Spinner, Modal, EmailPreview, ChipSelect, CategoryBadge, CATEGORY_LABELS, PageLoader, ConfirmDialog } from '../../components/ui';
import RichEditor from '../../components/RichEditor';
import api, { errMsg, API_BASE } from '../../services/api';
import { useAdmin } from '../../context/AuthContext';

const EMPTY_FILTERS = { audience: 'ALL', classNames: [], countries: [], cities: [], professions: [], rcuVerified: false };

export default function NotificationCompose() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { admin } = useAdmin();
  const [loaded, setLoaded] = useState(!id);
  const [draftId, setDraftId] = useState(id);
  const [layouts, setLayouts] = useState([]);
  const [options, setOptions] = useState({ classNames: [], countries: [], cities: [], professions: [] });
  const [form, setForm] = useState({ layoutId: '', category: 'GENERAL', subject: '', body: '' });
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [image, setImage] = useState(null);
  const [attachment, setAttachment] = useState(null);
  const [existing, setExisting] = useState({ imagePath: null, attachmentName: null });
  const [remove, setRemove] = useState({ image: false, attachment: false });
  const [count, setCount] = useState(null);
  const [busy, setBusy] = useState('');
  const [preview, setPreview] = useState(null);
  const [testEmail, setTestEmail] = useState('');
  const [confirmSend, setConfirmSend] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    api.get('/admin/layouts').then(r => {
      const usable = r.data.filter(l => l.category !== 'BIRTHDAY');
      setLayouts(usable);
      if (!id && usable.length) pickLayout(usable.find(l => l.category === 'GENERAL') || usable[0], true);
    });
    api.get('/admin/members/options').then(r => setOptions(r.data));
    if (id) {
      api.get(`/admin/notifications/${id}`).then(({ data }) => {
        if (data.status !== 'DRAFT') return navigate(`/admin/notifications/${id}`, { replace: true });
        setForm({ layoutId: data.layoutId || '', category: data.category, subject: data.subject, body: data.body });
        setFilters({ ...EMPTY_FILTERS, ...(data.filters || {}) });
        setExisting({ imagePath: data.imagePath, attachmentName: data.attachmentName });
        setLoaded(true);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => { setTestEmail(admin?.email || ''); }, [admin]);

  // Live recipient count
  useEffect(() => {
    const t = setTimeout(() => {
      api.post('/admin/notifications/recipient-count', { category: form.category, filters }).then(r => setCount(r.data)).catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [form.category, filters]);

  const imageUrl = useMemo(() => {
    if (image) return URL.createObjectURL(image);
    if (existing.imagePath && !remove.image) return `${API_BASE}/files/${existing.imagePath}`;
    return null;
  }, [image, existing.imagePath, remove.image]);
  useEffect(() => () => { if (image && imageUrl) URL.revokeObjectURL(imageUrl); }, [image, imageUrl]);

  function pickLayout(l, initial) {
    setForm(f => ({
      ...f,
      layoutId: l.id,
      category: l.category,
      subject: initial || !f.subject ? l.defaultSubject || '' : f.subject,
      body: initial || !f.body || f.body === '<p><br></p>' ? l.defaultBody || '' : f.body,
    }));
  }

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setF = (k, v) => setFilters(f => ({ ...f, [k]: v }));

  // Saves the draft (create or update) and returns its id
  const save = async (quiet) => {
    if (!form.subject.trim()) { toast.error('Please enter a subject'); return null; }
    const fd = new FormData();
    fd.append('layoutId', form.layoutId || '');
    fd.append('category', form.category);
    fd.append('subject', form.subject);
    fd.append('body', form.body);
    fd.append('filters', JSON.stringify(filters));
    if (image) fd.append('image', image);
    else if (remove.image) fd.append('removeImage', 'true');
    if (attachment) fd.append('attachment', attachment);
    else if (remove.attachment) fd.append('removeAttachment', 'true');
    const { data } = draftId ? await api.put(`/admin/notifications/${draftId}`, fd) : await api.post('/admin/notifications', fd);
    setExisting({ imagePath: data.imagePath, attachmentName: data.attachmentName });
    setImage(null);
    setAttachment(null);
    setRemove({ image: false, attachment: false });
    if (!quiet) toast.success('Draft saved');
    // Keep this page mounted; just reflect the new draft in the address bar
    if (!draftId) { setDraftId(data.id); window.history.replaceState(window.history.state, '', `/admin/notifications/${data.id}/edit`); }
    return data.id;
  };

  const run = async (key, fn) => {
    setBusy(key);
    try { await fn(); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(''); }
  };

  const doPreview = () => run('preview', async () => {
    const nid = await save(true);
    if (!nid) return;
    const { data } = await api.get(`/admin/notifications/${nid}/preview`);
    setPreview(data);
  });

  const doTest = () => run('test', async () => {
    const nid = await save(true);
    if (!nid) return;
    const { data } = await api.post(`/admin/notifications/${nid}/test`, { email: testEmail });
    toast.success(`Test email sent to ${data.to}`);
  });

  const doSend = async () => {
    const nid = await save(true);
    if (!nid) return;
    const { data } = await api.post(`/admin/notifications/${nid}/send`);
    toast.success(`Sending to ${data.count} member(s)…`);
    navigate(`/admin/notifications/${nid}`);
  };

  if (!loaded) return <PageLoader />;
  const hasAttachment = attachment || (existing.attachmentName && !remove.attachment);

  return (
    <div>
      <PageHeader title={draftId ? 'Edit draft' : 'New notification'} subtitle="Choose a layout, write your message, pick recipients, preview and send." />

      <div className="grid gap-6 lg:grid-cols-[1fr_340px]">
        <div className="space-y-5">
          <section className="card p-5">
            <h2 className="mb-3 font-semibold text-navy-900">1. Layout</h2>
            <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
              {layouts.map(l => (
                <button key={l.id} type="button" onClick={() => pickLayout(l)}
                  className={`rounded-lg border p-3 text-left transition-colors ${form.layoutId === l.id ? 'border-navy-800 bg-navy-50 ring-2 ring-gold-400' : 'border-gray-200 hover:border-navy-300'}`}>
                  <p className="text-sm font-medium text-navy-900">{l.name}</p>
                  <div className="mt-1"><CategoryBadge category={l.category} /></div>
                </button>
              ))}
            </div>
            <Field label="Notification type" className="mt-4" hint="Members who turned off this type won't receive it">
              <select className="input sm:w-64" value={form.category} onChange={e => set('category', e.target.value)}>
                {Object.entries(CATEGORY_LABELS).filter(([k]) => k !== 'BIRTHDAY').map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </Field>
          </section>

          <section className="card space-y-4 p-5">
            <h2 className="font-semibold text-navy-900">2. Message</h2>
            <Field label="Subject" required><input className="input" value={form.subject} onChange={e => set('subject', e.target.value)} placeholder="e.g. New committee for 2026/27" /></Field>
            <Field label="Message"><RichEditor value={form.body} onChange={v => set('body', v)} /></Field>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <p className="label">Image <span className="font-normal text-gray-400">(optional — poster, banner, photo)</span></p>
                {imageUrl ? (
                  <div className="relative">
                    <img src={imageUrl} alt="" className="max-h-48 w-full rounded-lg border object-contain" />
                    <button type="button" onClick={() => { setImage(null); setRemove(r => ({ ...r, image: true })); }} className="absolute right-2 top-2 rounded-full bg-white p-1 shadow hover:bg-red-50"><X size={14} /></button>
                  </div>
                ) : (
                  <label className="flex cursor-pointer flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed border-gray-300 p-6 text-sm text-gray-500 hover:border-navy-400">
                    <ImageIcon size={22} />Add image
                    <input type="file" accept="image/*" className="hidden" onChange={e => { setImage(e.target.files[0] || null); e.target.value = ''; }} />
                  </label>
                )}
              </div>
              <div>
                <p className="label">Attachment <span className="font-normal text-gray-400">(optional — PDF, letter, list)</span></p>
                {hasAttachment ? (
                  <div className="flex items-center gap-2 rounded-lg border bg-gray-50 p-3 text-sm">
                    <Paperclip size={16} className="text-gray-500" /><span className="flex-1 truncate">{attachment?.name || existing.attachmentName}</span>
                    <button type="button" onClick={() => { setAttachment(null); setRemove(r => ({ ...r, attachment: true })); }} className="rounded p-1 hover:bg-red-50"><X size={14} /></button>
                  </div>
                ) : (
                  <label className="flex cursor-pointer flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed border-gray-300 p-6 text-sm text-gray-500 hover:border-navy-400">
                    <Paperclip size={22} />Add attachment
                    <input type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png,.txt,.csv" className="hidden" onChange={e => { setAttachment(e.target.files[0] || null); e.target.value = ''; }} />
                  </label>
                )}
              </div>
            </div>
          </section>

          <section className="card space-y-4 p-5">
            <h2 className="font-semibold text-navy-900">3. Recipients</h2>
            <div className="flex gap-2">
              {[['ALL', 'All verified members'], ['FILTERED', 'A filtered group']].map(([v, l]) => (
                <button key={v} type="button" onClick={() => setF('audience', v)}
                  className={`rounded-lg border px-4 py-2 text-sm ${filters.audience === v ? 'border-navy-800 bg-navy-900 text-white' : 'border-gray-300 bg-white text-gray-700'}`}>{l}</button>
              ))}
            </div>
            {filters.audience === 'FILTERED' && (
              <div className="space-y-4 rounded-lg bg-gray-50 p-4">
                <Field label="Class"><ChipSelect options={options.classNames} value={filters.classNames} onChange={v => setF('classNames', v)} /></Field>
                <Field label="Country"><ChipSelect options={options.countries} value={filters.countries} onChange={v => setF('countries', v)} /></Field>
                <Field label="City"><ChipSelect options={options.cities} value={filters.cities} onChange={v => setF('cities', v)} /></Field>
                <Field label="Profession"><ChipSelect options={options.professions} value={filters.professions} onChange={v => setF('professions', v)} /></Field>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" className="h-4 w-4 accent-navy-900" checked={filters.rcuVerified} onChange={e => setF('rcuVerified', e.target.checked)} />Only members with a confirmed RCU card</label>
              </div>
            )}
          </section>
        </div>

        <aside className="space-y-4 lg:sticky lg:top-6 lg:self-start">
          <div className="card p-5">
            <p className="flex items-center gap-2 text-sm text-gray-500"><Users size={16} />Will be sent to</p>
            <p className="mt-1 text-4xl font-semibold text-navy-900">{count ? count.count : '…'}</p>
            <p className="text-sm text-gray-500">verified member{count?.count === 1 ? '' : 's'}</p>
            {count?.sample?.length > 0 && <p className="mt-2 text-xs text-gray-400">{count.sample.map(s => s.fullName).join(', ')}{count.count > count.sample.length ? '…' : ''}</p>}
            <p className="mt-3 text-xs text-gray-400">Unverified, disabled and opted-out members are excluded automatically.</p>
          </div>

          <div className="card space-y-3 p-5">
            <button onClick={() => run('save', () => save(false))} disabled={!!busy} className="btn-outline w-full">{busy === 'save' ? <Spinner size={16} /> : <Save size={16} />}Save draft</button>
            <button onClick={doPreview} disabled={!!busy} className="btn-outline w-full">{busy === 'preview' ? <Spinner size={16} /> : <Eye size={16} />}Preview</button>
            <div className="rounded-lg border p-3">
              <Field label="Send a test to"><input type="email" className="input" value={testEmail} onChange={e => setTestEmail(e.target.value)} /></Field>
              <button onClick={doTest} disabled={!!busy} className="btn-outline btn-sm mt-2 w-full">{busy === 'test' ? <Spinner size={14} /> : <FlaskConical size={14} />}Send test email</button>
            </div>
            <button onClick={() => setConfirmSend(true)} disabled={!!busy || !count?.count} className="btn-gold w-full py-2.5"><Send size={16} />Send now</button>
            {draftId && <button onClick={() => setConfirmDelete(true)} className="btn-ghost btn-sm w-full text-red-600"><Trash2 size={14} />Delete draft</button>}
          </div>
        </aside>
      </div>

      {preview && (
        <Modal title="Preview" wide onClose={() => setPreview(null)}>
          <p className="mb-3 text-sm"><span className="text-gray-500">Subject:</span> <strong>{preview.subject}</strong></p>
          <EmailPreview html={preview.html} height={620} />
          <p className="mt-2 text-xs text-gray-400">Placeholders like {'{{firstName}}'} are filled in with your name here, and each member's own name when sent.</p>
        </Modal>
      )}
      {confirmSend && (
        <ConfirmDialog title="Send notification?" confirmLabel={`Send to ${count?.count} member(s)`}
          message={`"${form.subject}" will be emailed to ${count?.count} verified member(s) now. This cannot be undone.`}
          onClose={() => setConfirmSend(false)}
          onConfirm={() => doSend().catch(err => toast.error(errMsg(err)))} />
      )}
      {confirmDelete && (
        <ConfirmDialog title="Delete draft?" danger confirmLabel="Delete" message="This draft and its image/attachment will be deleted."
          onClose={() => setConfirmDelete(false)}
          onConfirm={async () => { await api.delete(`/admin/notifications/${draftId}`); toast.success('Draft deleted'); navigate('/admin/notifications'); }} />
      )}
    </div>
  );
}
